import { useFlag } from '@reflag/react-sdk';
import { skipToken, useSuspenseQuery } from '@tanstack/react-query';

import type * as api from '@gitbook/api-client';
import {
    type PaletteCommandDef,
    PaletteCommandType,
    type PaletteGroupDef,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { type OverlayTriggerState, useDialogTriggerState } from '@gitbook/spine-react';

import { apiQuery, isSiteUpgradeAction, type UseUpgradeSite } from '../../../../state';
import { getPaidIndicatorBadgeProps } from '../../../Site';

export interface CustomFontState {
    paletteGroups: PaletteGroupDef[];
    newFontDialogTrigger: OverlayTriggerState;
    upgrade: UseUpgradeSite | null;
    value: api.CustomizationFontDefinitionInput | null;
    onChange: (customFont: api.CustomizationFontDefinitionInput) => void;
}

/**
 * State to manage custom fonts in the site customization flow.
 */
export function useCustomFontState(
    props: Pick<CustomFontState, 'upgrade' | 'value' | 'onChange'> & {
        organizationId: string;
    }
): CustomFontState | null {
    const { upgrade, organizationId, value, onChange } = props;
    const customFontsFeature = useFlag('CUSTOM_FONTS');
    const newFontDialogTrigger = useDialogTriggerState();
    const { data: customFontsData } = useSuspenseQuery(
        apiQuery('listCustomFonts', customFontsFeature.isEnabled ? { organizationId } : skipToken, {
            nullOnError: true,
        })
    );

    const customFonts = customFontsData?.items ?? [];
    const paletteGroups = [
        {
            key: 'add-font',
            commands: [
                {
                    key: 'upload',
                    title: 'Add custom font',
                    icon: Icon.AddNew,
                    trigger: upgrade ? upgrade.onPress : newFontDialogTrigger.open,
                    upgrade: isSiteUpgradeAction(upgrade),
                    badge: getPaidIndicatorBadgeProps({
                        upgrade: upgrade,
                    }),
                },
            ],
        },
        customFonts.length > 0
            ? {
                  key: 'custom-fonts',
                  title: 'Custom Fonts',
                  disabled: Boolean(upgrade),
                  commands: customFonts.map(
                      (item) =>
                          ({
                              key: item.id,
                              title: item.fontFamily,
                              upgrade: isSiteUpgradeAction(upgrade),
                              badge: getPaidIndicatorBadgeProps({
                                  upgrade,
                                  isFeatureActive: value?.id === item.id,
                              }),
                              trigger: () => {
                                  onChange(item);
                              },
                              ...(value?.id === item.id
                                  ? {
                                        type: PaletteCommandType.RADIO,
                                        selected: true,
                                    }
                                  : {}),
                          }) as PaletteCommandDef
                  ),
              }
            : null,
    ].filter((x) => x !== null);

    return customFontsFeature
        ? { paletteGroups, newFontDialogTrigger, upgrade, value, onChange }
        : null;
}
